import type { Configs, Environment, MenuItem, Organization, Page, SiteStructure } from '@/types';
import type { PayloadAction, ReducerCreators } from '@reduxjs/toolkit';

import { graphqlClient } from '@/api/graphqlClient';
import { pageStructureQuery } from '@/api/query';
import { transformSiteStructure } from '@/api/websiteApi';
import { asyncThunkCreator, buildCreateSlice } from '@reduxjs/toolkit';

import { loadState } from './localStorage';

export const createAppSlice = buildCreateSlice({
  creators: { asyncThunk: asyncThunkCreator },
});

const APP_REDUCER_NAME = 'app';

export const enum APP_REDUCER_STATUS {
  NOT_INITIATED = 'not_initiated',
  IDLE = 'idle',
  LOADING = 'loading',
  ERROR = 'error',
}

type AppState = {
  status: APP_REDUCER_STATUS;
  error: unknown;
  app: {
    concent: boolean;
    menuItems?: MenuItem[];
    pages?: Page[];
    organization?: Organization;
    environment?: Environment;
    configs?: Configs;
  };
};

const defaultState: AppState = {
  status: APP_REDUCER_STATUS.NOT_INITIATED,
  error: null,
  app: {
    concent: false,
    menuItems: undefined,
    pages: undefined,
    organization: undefined,
    environment: {
      color: process.env.REACT_APP_PRODUCT_ENVIRONMENT_COLOR,
      name: process.env.REACT_APP_PRODUCT_ENVIRONMENT_LABEL,
      version: process.env.REACT_APP_PRODUCT_VERSION || 'no defined',
      mode: process.env.NODE_ENV,
    },
    configs: {
      mapboxApiKey: process.env.REACT_APP_MAPBOX_API_KEY ?? '',
    },
  },
};

const loadStorageUser = () => {
  const persistedState = loadState(APP_REDUCER_NAME);

  if (persistedState) {
    const time = new Date().getTime() / 1000;

    if (persistedState.expireOn > time) {
      return persistedState;
    }
  }

  return defaultState;
};

const fetchSiteStructure = (creator: ReducerCreators<AppState>) =>
  creator.asyncThunk(
    async () => {
      const response = await graphqlClient.query({
        query: pageStructureQuery,
      });

      return transformSiteStructure(response.data);
    },
    {
      pending: (state: AppState) => {
        state.status = APP_REDUCER_STATUS.LOADING;
      },
      rejected: (state: AppState, action: any) => {
        state.status = APP_REDUCER_STATUS.ERROR;
        state.error = action.error.message || 'Failed to fetch data';
      },
      fulfilled: (state: AppState, action: any) => {
        state.status = APP_REDUCER_STATUS.IDLE;
        state.app = {
          ...state.app,
          ...action.payload,
        };
      },
    }
  );

export const appSlice = createAppSlice({
  name: APP_REDUCER_NAME,
  initialState: loadStorageUser(),
  reducers: (create) => ({
    initSite: fetchSiteStructure(create),
    changeConsent: create.reducer<boolean>((state, action: PayloadAction<boolean>) => {
      state.app.concent = action.payload;
    }),
    updatePageStructure: create.reducer<SiteStructure>(
      (state, action: PayloadAction<SiteStructure>) => {
        state.app = {
          ...state.app,
          ...action.payload,
        };
      }
    ),
  }),
  selectors: {
    appStatus: (state: AppState): APP_REDUCER_STATUS => state.status,
    appError: (state: AppState): any => state.error,
    appConsent: (state: AppState): boolean => state.app.concent,
    appPages: (state: AppState): Page[] | undefined => state.app.pages,
    appMenus: (state: AppState): MenuItem[] | undefined => state.app.menuItems,
    appOrganization: (state: AppState): Organization | undefined => state.app.organization,
    appEnvironment: (state: AppState): Environment | undefined => state.app.environment,
    appHomePage: (state: AppState): string => state.app.menuItems?.[0].uri || '',
    appMapApi: (state: AppState): string => state.app.configs?.mapboxApiKey || '',
    appIsDevMode: (state: AppState): boolean =>
      state.app.environment?.mode === 'development' || false,
  },
});

// ACTIONS
export const { initSite, changeConsent, updatePageStructure } = appSlice.actions;

// SELECTORS
export const {
  appStatus,
  appError,
  appConsent,
  appPages,
  appMenus,
  appOrganization,
  appEnvironment,
  appHomePage,
  appMapApi,
  appIsDevMode,
} = appSlice.selectors;

export default appSlice.reducer;
